<template>
  <Section
    :id="id"
    :header="t('common.basicInfo')"
    class="basic-information-entry p-0"
    data-class="flex flex-column"
  >
    <Card class="order-entry-information-card">
      <template #content>
        <div class="flex flex-wrap flex-gap-1 information-section">
          <InputWrapper
            v-slot="{ ariaProps, inputClasses, inputId, invalid }"
            input-id="direction"
            :label="t('shipments.directions')"
            :errors="v$.direction.$errors"
          >
            <DirectionSelect
              :id="inputId"
              v-model="v$.direction.$model"
              v-bind="ariaProps"
              :class="inputClasses"
              :invalid="invalid"
              :disabled="disabled"
              filter
              show-clear
            />
          </InputWrapper>

          <InputWrapper
            v-slot="{ ariaProps, inputClasses, inputId, invalid }"
            input-id="billing-terms"
            :label="t('shipments.billingTerms')"
            :errors="v$.billingTerms.$errors"
          >
            <BillingTermsSelect
              :id="inputId"
              v-model="v$.billingTerms.$model"
              v-bind="ariaProps"
              :class="inputClasses"
              :invalid="invalid"
              :disabled="disabled"
              filter
              show-clear
            />
          </InputWrapper>

          <InputWrapper
            v-slot="{ ariaProps, inputId }"
            input-id="requested-mode"
            :label="t('common.requestedMode')"
            optional
          >
            <RygenDropdown
              :id="inputId"
              v-model="internalValue.requestedMode"
              v-bind="ariaProps"
              :disabled="disabled"
              :options="availableModes"
              :placeholder="
                t('common.selectParam', {
                  param: t('common.requestedMode'),
                })
              "
              option-label="label"
              option-value="value"
              filter
              show-clear
            />
          </InputWrapper>

          <InputWrapper
            v-slot="{ ariaProps, inputId }"
            input-id="equipment-type"
            :label="
              t('common.paramTypes', { param: t('referenceData.equipment') })
            "
            optional
          >
            <RygenDropdown
              :id="inputId"
              v-model="internalValue.equipmentType"
              v-bind="ariaProps"
              :disabled="disabled"
              :loading="equipmentTypes.loading"
              :options="equipmentTypes.data"
              :placeholder="
                t('common.selectParam', {
                  param: t('common.paramTypes', {
                    param: t('referenceData.equipment'),
                  }),
                })
              "
              option-label="name"
              filter
              show-clear
            />
          </InputWrapper>
        </div>

        <div
          class="flex flex-gap-1 flex-column information-section flex-grow-1"
        >
          <InputWrapper
            v-slot="{ ariaProps, inputId }"
            input-id="internal-notes"
            :label="t('orders.paramNotes', { param: t('orders.internal') })"
            optional
          >
            <Textarea
              :id="inputId"
              v-model="internalValue.internalNotes"
              v-bind="ariaProps"
              :disabled="disabled"
              rows="5"
              cols="30"
              auto-resize
              :placeholder="
                t('orders.paramNotes', { param: t('orders.internal') })
              "
            />
          </InputWrapper>
          <InputWrapper
            v-slot="{ ariaProps, inputId }"
            input-id="carrier-notes"
            :label="t('orders.paramNotes', { param: t('carriers.carriers') })"
            optional
          >
            <Textarea
              :id="inputId"
              v-model="internalValue.carrierNotes"
              v-bind="ariaProps"
              :disabled="disabled"
              rows="5"
              cols="30"
              auto-resize
              :placeholder="
                t('orders.paramNotes', { param: t('carriers.carriers') })
              "
            />
          </InputWrapper>
        </div>

        <div class="flex flex-gap-1 information-section">
          <WrappedSwitch
            v-if="$hasAuthority('order', 'swc')"
            id="swc"
            v-model="internalValue.swc"
            :label="t('common.isSwcParam', { param: parentEntityLabel })"
            :disabled="disabled || internalValue.customerRouted"
          >
            <template #label="{ inputId }">
              <i18n-t keypath="common.isSwcParam" tag="label" :for="inputId">
                <img
                  src="@rygen/rygen-ui/assets/img/swc.svg"
                  alt=""
                  class="swc-icon"
                  width="25"
                  height="14"
                />

                <template #param>
                  {{ parentEntityLabel }}
                </template>
              </i18n-t>
            </template>
          </WrappedSwitch>

          <WrappedSwitch
            id="customer-routed"
            v-model="internalValue.customerRouted"
            :label="
              t('common.isCustomerRoutedParam', { param: parentEntityLabel })
            "
            :disabled="disabled || internalValue.swc"
          >
            <template #label="{ inputId }">
              <i18n-t
                keypath="common.isCustomerRoutedParam"
                tag="label"
                :for="inputId"
              >
                <img
                  src="@/assets/img/customerRouted.svg"
                  alt=""
                  class="customer-routed-icon"
                  width="20"
                  height="10"
                />

                <template #param>
                  {{ parentEntityLabel }}
                </template>
              </i18n-t>
            </template>
          </WrappedSwitch>

          <WrappedSwitch
            id="skip-auto-tender"
            v-model="internalValue.skipAutoTender"
            :label="
              t('common.skipAutoActionParam', { param: parentEntityLabel })
            "
            :disabled="disabled"
          >
          </WrappedSwitch>

          <WrappedSwitch
            id="hot"
            v-model="internalValue.hot"
            :label="t('common.hot')"
            :disabled="disabled"
          >
          </WrappedSwitch>
        </div>

        <div class="flex flex-column flex-gap-1 information-section">
          <OrderEntryValueOfGoods
            id="value-of-goods"
            v-model:order-value="internalValue.valueOfGoods"
            v-model:line-items="internalValue.lineItems"
            :disabled="disabled"
          />
        </div>
      </template>
    </Card>
  </Section>
</template>

<script lang="ts" setup>
import type { PropType } from 'vue';
import { computed, onMounted, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import {
  required,
  useModelValue,
  useValidations,
} from '@rygen/rygen-ui/composables';
import { mapEnumToTranslatedDropdownValues } from '@rygen/rygen-ui/i18n';
import { Mode } from '@rygen/rygen-ui/model';

import { ManualShipment, Order } from '@/model';
import { useEquipmentTypeStore } from '@/stores/EquipmentType';
import { useParentEntityStore } from '@/stores/ParentEntity';

import OrderEntryValueOfGoods from '@/components/order/OrderEntryValueOfGoods.vue';
import BillingTermsSelect from '@rygen/rygen-ui/components/form/BillingTermsSelect.vue';
import DirectionSelect from '@rygen/rygen-ui/components/form/DirectionSelect.vue';
import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';
import RygenDropdown from '@rygen/rygen-ui/components/form/RygenDropdown.vue';
import WrappedSwitch from '@rygen/rygen-ui/components/form/WrappedSwitch.vue';
import Section from '@rygen/rygen-ui/components/ui/Section.vue';
import Card from 'primevue/card';
import Textarea from 'primevue/textarea';

const props = defineProps({
  modelValue: {
    type: Object as PropType<Order | ManualShipment>,
    default: () => new Order(),
  },
  id: {
    type: String,
    required: true,
  },
  disabled: {
    type: Boolean,
    default: () => false,
  },
});
const { modelValue, id, disabled } = toRefs(props);
const emit = defineEmits<{
  (event: 'update:model-value', newVal: Order): void;
}>();

const { equipmentTypes, loadEquipmentTypes } = useEquipmentTypeStore();
const { t } = useI18n();
const { internalValue } = useModelValue(modelValue, {
  emit,
  factoryMethod: (opts: Partial<Order | ManualShipment>) => {
    const Clazz = Object.getPrototypeOf(opts).constructor;
    return new Clazz(opts);
  },
});
const { owningClient, parentEntity } = useParentEntityStore();

const availableModes = mapEnumToTranslatedDropdownValues(Mode, 'Mode');

const rules = {
  direction: {
    required: required(t('shipments.directions')),
  },
  billingTerms: {
    required: required(t('shipments.billingTerms')),
  },
  internalNotes: {},
  carrierNotes: {},
};
const { v$ } = useValidations(internalValue, rules);

const isOrder = computed(() => parentEntity.value instanceof Order);

const parentEntityLabel = computed(() =>
  isOrder.value ? t('orders.orders') : t('shipments.shipments'),
);

onMounted(async () => {
  if (!disabled.value && owningClient.value) {
    loadEquipmentTypes();
  }
});
</script>

<style>
.basic-information-entry {
  .p-card {
    .p-card-body {
      padding: 0;

      .p-card-content {
        display: flex;
        flex-flow: column;

        .information-section {
          padding: var(--p-card-body-padding);

          &:first-of-type {
            .input-wrapper {
              width: calc(50% - var(--gap-1) / 2);
            }
          }

          &:not(:last-of-type) {
            border-bottom: var(--divider);
          }

          .swc-icon {
            display: inline-block;
            vertical-align: middle;
          }

          .customer-routed-icon {
            display: inline-block;
            vertical-align: middle;
          }
        }
      }
    }
  }
}
</style>
